import { BLOCK_LIST_ITEM, BLOCK_LIST_OL, BLOCK_LIST_TASKS, list } from '@gitbook/doc-core';
import { Block, type Change, TextRange } from '@gitbook/slate';
import type { NodeProps, Plugin } from '@gitbook/slate-react';

import { isBackspace, isEnter, isShiftTab, isTab } from '../common';

/**
 * Plugin to handle keyboard interactions in lists.
 */
export const listPlugin: Plugin = {
    onKeyDown,
    shouldNodeComponentUpdate,

    prepareBlockForTransport: (block, parent) => {
        // List Items don't include anything indicating the type of the list they are in.
        // When transporting a list item, we wrap it in a block of the same type as the parent.
        // e.g. <list-item>hello</list-item> becomes <list-ordered><list-item>hello</list-item></list-ordered>
        if (block.type === BLOCK_LIST_ITEM) {
            return Block.create({
                type: parent.type,
                data: parent.data,
                isVoid: parent.isVoid,
                fragments: parent.fragments,
                nodes: [block],
            });
        }

        return null;
    },
};

/**
 * Rendering of list-item depends on the parent.
 */
function shouldNodeComponentUpdate(props: NodeProps, nextProps: NodeProps): boolean | null {
    if (props.node.type === BLOCK_LIST_ITEM) {
        const { parent: prevParent, node: prevNode } = props;
        const { parent: nextParent, node: nextNode } = nextProps;

        if (prevParent.object !== 'block' || nextParent.object !== 'block') {
            throw new Error('Parent of list-item must be a block');
        }

        // List-item in ordered lists should be re-rendered when their position in the parent is changing
        if (
            prevParent.type === BLOCK_LIST_OL &&
            prevParent.getChildIndex(prevNode.key) !== nextParent.getChildIndex(nextNode.key)
        ) {
            return true;
        }
    }

    return null;
}

function onKeyDown(event: KeyboardEvent, change: Change) {
    if (isEnter(event)) {
        return onEnter(event, change);
    }
    if (isTab(event) || isShiftTab(event)) {
        return onTab(event, change);
    }
    if (isBackspace(event)) {
        return onBackspace(event, change);
    }
}

/**
 * User pressed Enter in an editor
 *
 * Enter in a list item should split the list item
 * Enter in an empty list item should remove it
 * Shift+Enter in a list item should make a new line
 */
function onEnter(event: KeyboardEvent, change: Change): void | any {
    // Pressing Shift+Enter
    // should split block normally
    if (event.shiftKey) {
        return undefined;
    }

    const { value } = change;
    const currentItem = list.getCurrentItem(value);

    // Not in a list
    if (!currentItem) {
        return undefined;
    }

    event.preventDefault();

    // If expanded, delete first.
    if (value.isExpanded) {
        change.delete();
    }

    if (currentItem.isEmpty) {
        // Block is empty, we exit the list
        if (list.getItemDepth(value) > 1) {
            return list.decreaseItemsDepth(change);
        }
        // Exit list
        return list.unwrapList(change);
    }
    // Split list item
    list.splitListItem(change);

    // Make sure the new item is not checked by default when it is a task list
    const parentList = list.getListForItem(change.value, currentItem);
    if (parentList && parentList.type === BLOCK_LIST_TASKS) {
        const newTaskItem = change.value.document.getNextSibling(currentItem.key);
        return newTaskItem ? list.checkTaskItem(change, newTaskItem.key, false) : change;
    }

    return change;
}

/**
 * User pressed Delete in an editor
 */
function onBackspace(event: KeyboardEvent, change: Change): void | any {
    const { value } = change;
    const { startOffset, selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return undefined;
    }
    // Only unwrap...
    // ... with a collapsed selection
    if (selection.isExpanded) {
        return undefined;
    }

    // ... when at the beginning of nodes
    if (startOffset > 0) {
        return undefined;
    }

    // ... in a list
    const currentItem = list.getCurrentItem(value);

    if (!currentItem) {
        return undefined;
    }

    // ... more precisely at the beginning of the current item
    if (!selection.isAtStartOf(currentItem)) {
        return undefined;
    }

    event.preventDefault();
    return list.unwrapList(change);
}

/**
 * User pressed Tab in an editor.
 * Tab       -> Increase item depth if inside a list item
 * Shift+Tab -> Decrease item depth if inside a list item
 */
function onTab(event: KeyboardEvent, change: Change): void | any {
    // Shift+tab reduce depth
    if (event.shiftKey) {
        event.preventDefault();

        return list.decreaseItemsDepth(change);
    }

    // Tab increases depth
    event.preventDefault();

    return list.increaseItemsDepth(change);
}
